import * as React from 'react';
import { Theme } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import { ITreeSelectProps } from './EDTreeSelect';
import EDTreeSelectNestedListItem from './EDTreeSelectNestedListItem';
import {
  updateChecked,
  updateOpen,
  getSublistCheckStatus,
  ITreeSection,
  SectionCheckStatus,
} from './TreeSelectUtils';

import EDTreeSelectListItemBase from './EDTreeSelectListItemBase';

interface IProps<T> extends ITreeSelectProps<T> {
  section: ITreeSection;
  selections: ITreeSection[];
  sectionIndex: number;
  level: number;
  classes?: any;
}

const styles = (
  theme: Theme,
  _params: any,
  classes: Record<'listItem', string>
) => ({
  listItem: {},
  hoverButtons: {
    opacity: 0,
    transition: 'opacity 100ms ease',
    [`.${classes.listItem}:hover &`]: {
      opacity: 1,
    },
  },
  itemContainer: {
    paddingTop: 0,
    paddingBottom: 0,
    whiteSpace: 'normal',
    display: 'block',
  },
  fontSize: {
    fontSize: '0.875rem',
    fontWeight: 400,
  },
  fontBold: {
    fontWeight: theme.typography.fontWeightBold,
  },
  checkboxHighlightedText: {
    wordBreak: 'break-word',
  },
  analysItemContainer: {
    position: 'relative',
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  icon: {
    paddingRight: theme.spacing(2),
  },
  newFieldButton: {
    marginRight: theme.spacing(3),
  },
  listItemEndContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  checkboxRoot: {
    color: theme.palette.secondary.main,
  },
  checkboxFormRoot: {
    flexShrink: 0,
  },
  checkboxLabel: {
    overflow: 'hidden',
  },
  horizontalNodeLine: {
    '&:before': {
      position: 'absolute',
      left: `calc(-${theme.spacing(2)})`,
      width: theme.spacing(2),
      content: '""',
      borderBottom: '2px solid #d7d7d7',
    },
  },
});

function EDTreeSelectListItem<T>(props: IProps<T>) {
  const {
    section,
    selections,
    classes,
    onCheck,
    sectionsUpdate,
    disableSectionsClick,
    ...rest
  } = props;
  const handleCheck = React.useCallback(
    (suggestion: ITreeSection<T>, index: number) => (value: boolean) => {
      const { updatedSections, checkedSections } = updateChecked(
        section,
        props.sections,
        value,
        suggestion
      );
      if (onCheck) {
        sectionsUpdate(
          onCheck(updatedSections, checkedSections, value, suggestion)
        );
      } else {
        sectionsUpdate(updatedSections);
      }
    },
    [section, props.sections, onCheck, sectionsUpdate]
  );
  const updateOpenSection = React.useCallback(
    (section: ITreeSection) => () => {
      sectionsUpdate(updateOpen(section, props.sections));
    },
    [props.sections, sectionsUpdate]
  );

  const sectionClickIsDisabled = React.useCallback(
    (suggestion: ITreeSection<T>) => {
      if (!suggestion.subList) {
        return false;
      }
      if (typeof disableSectionsClick === 'function') {
        return disableSectionsClick(suggestion);
      }
      return Boolean(disableSectionsClick);
    },
    [disableSectionsClick]
  );

  return (
    <>
      {props.selections.map((suggestion, index) => {
        const disableSectionClick = sectionClickIsDisabled(suggestion);

        const baseCase = props.section.value.split('|').length === 1;
        const section: ITreeSection = {
          ...suggestion,
          value: `${
            baseCase ? props.sectionIndex : props.section.value
          }|subList|${index}`,
        };

        const sublistCheckStatus = getSublistCheckStatus(
          suggestion,
          props.isChecked
        );
        return (
          !suggestion.hidden && (
            <React.Fragment key={index}>
              <EDTreeSelectListItemBase
                parentSection={props.section}
                section={suggestion}
                showPointerLines={props.showPointerLines}
                level={props.level}
                checked={sublistCheckStatus === SectionCheckStatus.FullyChecked}
                indeterminate={
                  sublistCheckStatus === SectionCheckStatus.PartiallyChecked
                }
                checkBoxDisabled={
                  disableSectionClick || suggestion.disabled?.value
                }
                checkBoxDisabledReason={
                  disableSectionClick
                    ? 'Nod ej valbar'
                    : suggestion.disabled?.tooltip
                }
                checkBoxTooltip={
                  suggestion.subList
                    ? sublistCheckStatus === SectionCheckStatus.FullyChecked
                      ? 'Ta bort alla'
                      : 'Fyll i alla'
                    : undefined
                }
                checkboxNoClickPropagation={
                  suggestion.open ||
                  sublistCheckStatus === SectionCheckStatus.FullyChecked ||
                  !suggestion.subList
                }
                fontBold={
                  props.parentSectionBoldText && Boolean(suggestion.subList)
                }
                searchValue={props.searchValue}
                onListItemClick={
                  suggestion.subList && updateOpenSection(section)
                }
                onCheck={
                  !disableSectionClick
                    ? handleCheck(suggestion, index)
                    : undefined
                }
                renderSecondaryText={props.renderSecondaryText}
                showListItemButtons={props.showListItemButtons}
                hoverChecklistButtons={props.hoverChecklistButtons}
                renderListItemButtons={props.renderListItemButtons}
              />
              {suggestion.subList && suggestion.open && (
                <EDTreeSelectNestedListItem
                  {...rest}
                  suggestion={suggestion}
                  section={props.section}
                  sectionIndex={props.sectionIndex}
                  suggestionIndex={index}
                  onCheck={onCheck}
                  sectionsUpdate={sectionsUpdate}
                  disableSectionsClick={disableSectionsClick}
                />
              )}
            </React.Fragment>
          )
        );
      })}
    </>
  );
}

export default withStyles(EDTreeSelectListItem, styles, {
  uniqId: 'a89203a7-3aa0-4773-a652-66352264812e',
});
